import type { UseQueryResult } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import React, { createContext, useContext, useEffect } from 'react';
import type {
	PeticeClenPeticnihoVyboruSeznamDto,
	PeticePeticeDto,
} from '@gov-nx/api';
import {
	ePetitionCreateQuery,
	ePetitionDeleteQuery,
	ePetitionDetailQuery,
	ePetitionMembersQuery,
	ePetitionUpdateQuery,
	useAxiosInstance,
	usePoMutation,
	usePoQueries,
} from '@gov-nx/api';
import type { GovError } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import {
	useApplicantLoader,
	useCountryCodes,
	usePersonCommunicationData,
	useProcessControl,
	useServiceAuthorization,
	useWizard,
	AUTHORIZATION_SERVICE_NAME_EPETICE_ZALOZIT,
} from '@gov-nx/core/hooks';
import {
	useFormBuilderWithConsent,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { has } from '@gov-nx/core/types';
import { setContactDetailsValues, useBoolean } from '@gov-nx/utils/common';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';
import type { PageCode } from '../../definitions/codes';
import { useROSCommunicationQuery } from '../../nastaveni/kontaktni-udaje-ros/useROSCommunicationQuery';
import {
	FormInstanceStep1,
	FormInstancePOStep2,
	prepareSubmitData,
	FormInstanceFOStep2,
} from '../FormDefinitions';
import { isPetitionEditable } from '../detail/utils';
import type { EPetitionNewControls } from '../nova/context.types';
import { useAddressLookup } from '../useAddressLookup';
import { usePetitionAttachments } from '../usePetitionAttachments';
import { usePetitionMemberAddressLookup } from '../usePetitionMemberAddressLookup';
import type { EPeticeFormData } from '../wizzard.types';
import type { EPetitionEditContext } from './context.types';
import { setPetitionDetailsValues } from './formUtils';

const EPeticeEditContext = createContext<Nullable<EPetitionEditContext>>(null);

interface EPeticeEditContextProviderProps {
	petitionId: number;
	children: React.ReactNode;
	code: PageCode;
}

export const EPeticeEditContextProvider = ({
	petitionId,
	code,
	children,
}: EPeticeEditContextProviderProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const { toastMessageSuccess } = useMessageEvents();
	const { axiosInstance } = useAxiosInstance();
	const { setControls, controls } = useProcessControl<EPetitionNewControls>({
		initialLoading: true,
		createLoading: false,
		saveLoading: false,
		deleteLoading: false,
	});
	const queryClient = useQueryClient();
	const personCommunicationData = usePersonCommunicationData();
	const petitionAttachments = usePetitionAttachments(petitionId);
	const authContext = useAutorizaceContext();
	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME_EPETICE_ZALOZIT);

	const createFormDefinition = useFormBuilderWithConsent<
		Record<never, never>,
		unknown
	>([], {
		isDisabled: controls.createLoading,
		isLowAuthorization,
	});

	const formStep1 = FormInstanceStep1({ code });
	const formStep2PO = FormInstancePOStep2({
		code,
		petitionAttachmentsOptions: petitionAttachments.options,
	});
	const formStep2FO = FormInstanceFOStep2({
		code,
		petitionAttachmentsOptions: petitionAttachments.options,
	});

	const addressLookup = useAddressLookup({
		code,
		onSubmit: ({ address }) => {
			formStep2FO.formDefinition.formMethods.setValue('adresa', address);
			personCommunicationData.control.isEditingAddress.setFalse();
		},
	});

	const [petitionQuery, petitionMembersQuery] = usePoQueries<
		[
			UseQueryResult<PeticePeticeDto>,
			UseQueryResult<PeticeClenPeticnihoVyboruSeznamDto>,
		]
	>({
		queries: [
			{
				queryKey: ['epetice-detail', petitionId],
				queryFn: () => ePetitionDetailQuery(axiosInstance, petitionId),
				refetchOnWindowFocus: false,
				retry: 0,
				onError: (initialError: unknown) => {
					setControls({
						initialLoading: false,
						initialError: initialError as GovError,
					});
				},
			},
			{
				queryKey: ['epetice-members', petitionId],
				queryFn: () => ePetitionMembersQuery(axiosInstance, petitionId),
				refetchOnWindowFocus: false,
				retry: 0,
				onError: (initialError: unknown) => {
					setControls({
						initialLoading: false,
						initialError: initialError as GovError,
					});
				},
			},
		],
	});
	const rosCommunicationQuery = useROSCommunicationQuery();

	const { isFetched, stringToPhoneShape } = useCountryCodes();

	const zakladatelTyp = petitionQuery.data?.zakladatel
		? petitionQuery.data.zakladatel.pravnickaOsoba
			? 'PO'
			: 'FO'
		: undefined;

	const applicant = useApplicantLoader({
		onError: (initialError) => {
			setControls({ initialLoading: false, initialError });
		},
		onLoaded: () => null,
	});

	const setFormValues = ({
		zakladatel,
		nazev,
		perex,
		text,
		adresat,
		kontaktniUdaje,
	}: PeticePeticeDto) => {
		nazev && formStep1.formMethods.setValue('nazev', nazev);
		zakladatelTyp &&
			formStep1.formMethods.setValue('zakladatelTyp', zakladatelTyp);

		if (formStep2FO && zakladatelTyp === 'FO') {
			setPetitionDetailsValues(formStep2FO.formDefinition, {
				perex,
				text,
				adresat,
			});

			const phone =
				kontaktniUdaje?.telefon ?? personCommunicationData.phoneVerified;

			setContactDetailsValues(formStep2FO.formDefinition, {
				_useEmail: !!kontaktniUdaje?.email,
				email: kontaktniUdaje?.email ?? personCommunicationData.emailVerified,
				_usePhone: !!kontaktniUdaje?.telefon,
				phone: phone ? stringToPhoneShape(phone) : undefined,
			});

			formStep2FO.formDefinition.formMethods.setValue(
				'priloha',
				petitionAttachments.files
			);

			formStep2FO.formDefinition.formMethods.setValue(
				'_useAddress',
				!!kontaktniUdaje?.adresa?.kod
			);
			kontaktniUdaje?.adresa?.kod &&
				formStep2FO.formDefinition.formMethods.setValue(
					'adresa',
					kontaktniUdaje?.adresa
				);
			kontaktniUdaje?.adresa?.kod &&
				formStep2FO.formDefinition.formMethods.setValue(
					'adresa.kod',
					kontaktniUdaje?.adresa.kod
				);
			kontaktniUdaje?.adresa?.kod &&
				formStep2FO.formDefinition.formMethods.setValue(
					'adresa.adresaText',
					kontaktniUdaje?.adresa.adresaText
				);
		}

		if (zakladatelTyp === 'PO') {
			setPetitionDetailsValues(formStep2PO.formDefinition, {
				perex,
				text,
				adresat,
			});

			const ico =
				zakladatel?.pravnickaOsoba?.ico ??
				applicant.businessPerson?.seznam?.[0].ico;

			ico &&
				formStep2PO.formDefinition.formMethods.setValue('zakladatelPoIco', ico);

			formStep2PO.formDefinition.formMethods.setValue(
				'priloha',
				petitionAttachments.files
			);

			if (ico) {
				const contact = rosCommunicationQuery.getRosContact(ico);
				const phone = kontaktniUdaje?.telefon ?? contact?.telefonniCislo;

				setContactDetailsValues(formStep2PO.formDefinition, {
					_useEmail: !!kontaktniUdaje?.email,
					_usePhone: !!kontaktniUdaje?.telefon,
					email: kontaktniUdaje?.email ?? contact?.email,
					phone: phone ? stringToPhoneShape(phone) : undefined,
				});
			}
		}
	};

	const setPetitionMembers = ({
		seznam,
	}: PeticeClenPeticnihoVyboruSeznamDto) => {
		formStep2FO.formDefinition.formMethods.setValue(
			'clenovePeticnihoVyboru',
			seznam ?? []
		);
	};

	useEffect(
		() => {
			if (
				petitionQuery.data &&
				zakladatelTyp &&
				!controls.initialLoading &&
				rosCommunicationQuery.isFetched
			) {
				setFormValues(petitionQuery.data);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
		[
			zakladatelTyp,
			petitionQuery.data,
			controls.initialLoading,
			rosCommunicationQuery.isFetched,
			applicant.businessPerson?.seznam,
		]
	);

	useEffect(() => {
		if (petitionMembersQuery.data) {
			setPetitionMembers(petitionMembersQuery.data);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [petitionMembersQuery.data]);

	useEffect(
		() => {
			if (
				petitionQuery.isFetched &&
				petitionMembersQuery.isFetched &&
				isFetched &&
				applicant.individualPerson &&
				applicant.businessPerson &&
				controls.initialLoading
			) {
				setControls({ initialLoading: false });
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
		[
			petitionQuery.isFetched,
			petitionMembersQuery.isFetched,
			isFetched,
			controls.initialLoading,
			applicant.individualPerson,
			applicant.businessPerson,
		]
	);

	const isSubmitConfirmModalOpen = useBoolean(false);

	const deleteMutation = usePoMutation({
		mutationFn: async () => {
			setControls({ processError: null, deleteLoading: true });
			return ePetitionDeleteQuery(axiosInstance, petitionId);
		},
		onSuccess: async () => {
			toastMessageSuccess(tsn('formular.zprava.smazano'));

			await queryClient.invalidateQueries({ queryKey: ['epetice-seznam'] });
			wizard.resetForms();
			setControls({ deleteLoading: false });
		},
		onError: (error) => {
			setControls({ processError: error, deleteLoading: false });
		},
	});

	const saveMutation = usePoMutation({
		mutationFn: async () => {
			setControls({ processError: null, saveLoading: true });

			return ePetitionUpdateQuery({
				axiosInstance,
				petitionId,
				body: prepareSubmitData(
					formStep1.formMethods.getValues(),
					formStep2?.formDefinition.formMethods.getValues()
				),
			});
		},
		onSuccess: async () => {
			toastMessageSuccess(tsn('formular.zprava.ulozeno'));

			await queryClient.invalidateQueries({ queryKey: ['epetice-seznam'] });
			await queryClient.invalidateQueries({
				queryKey: ['epetice-detail', petitionId],
			});
			await queryClient.invalidateQueries({
				queryKey: ['epetice-members', petitionId],
			});
			await petitionAttachments.invalidate();

			setControls({ saveLoading: false });
		},
		onError: (error) => {
			setControls({ processError: error, saveLoading: false });
		},
	});

	const createMutation = usePoMutation({
		mutationFn: async () => {
			setControls({ processError: null, createLoading: true });
			await ePetitionUpdateQuery({
				axiosInstance,
				petitionId,
				body: prepareSubmitData(
					formStep1.formMethods.getValues(),
					formStep2?.formDefinition.formMethods.getValues()
				),
			});

			return ePetitionCreateQuery(axiosInstance, petitionId);
		},
		onSuccess: async (response) => {
			setControls({ createLoading: false });

			const polozkaAutorizaceId = response?.headers?.['x-autorizaceid'];

			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(tsn('formular.zprava.zalozeno'));
			};

			if (authContext && polozkaAutorizaceId) {
				isSubmitConfirmModalOpen.setFalse();
				authContext.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME_EPETICE_ZALOZIT,
					polozkaAutorizaceId,
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}

			await queryClient.invalidateQueries({ queryKey: ['epetice-seznam'] });
			await queryClient.invalidateQueries({
				queryKey: ['epetice-detail', petitionId],
			});
			await queryClient.invalidateQueries({
				queryKey: ['epetice-members', petitionId],
			});
			await petitionAttachments.invalidate();

			wizard.resetForms();
		},
		onError: (error) => {
			setControls({ processError: error, createLoading: false });
		},
	});

	const petitionMemberAddressLookup = usePetitionMemberAddressLookup({
		code,
		onSubmit: ({ formData, address }) => {
			isPetitionMemberAddressLookupModalOpen.setFalse();

			const existing =
				formStep2FO.formDefinition.formMethods.getValues(
					'clenovePeticnihoVyboru'
				) ?? [];

			formStep2FO.formDefinition.formMethods.setValue(
				'clenovePeticnihoVyboru',
				[
					...existing,
					{
						jmeno: formData.jmeno,
						prijmeni: formData.prijmeni,
						adresa: {
							kod: address?.kod,
							adresaText: address?.adresaText,
						},
					},
				]
			);
		},
	});

	const formStep2 = zakladatelTyp
		? zakladatelTyp === 'PO'
			? formStep2PO
			: formStep2FO
		: undefined;

	const wizard = useWizard<EPeticeFormData>({
		initialIndexOpen: [1],
		steps: [formStep1, formStep2?.formDefinition ?? {}, {}, {}],
		onSubmit: async (_, onSuccess) =>
			createMutation.mutate(undefined, {
				onSuccess,
			}),
		onStepInvalid: (index, errors) => {
			if (index === 1) {
				personCommunicationData.onFormValidated(errors);
			}
		},
	});

	const selectedIcNumber =
		zakladatelTyp === 'PO'
			? formStep2PO?.formDefinition.formMethods.watch('zakladatelPoIco')
			: undefined;

	const selectIcNumberOptions =
		applicant.businessPerson?.seznam?.filter(has('ico')).map((subject) => ({
			value: subject.ico,
			label: `${subject.nazevSpolecnosti}, IČ: ${subject.ico}`,
		})) ?? [];

	const isPetitionMemberAddressLookupModalOpen = useBoolean(false);
	const isDeleteConfirmModalOpen = useBoolean(false);

	useEffect(
		() => {
			if (!isPetitionMemberAddressLookupModalOpen.value) {
				petitionMemberAddressLookup.formDefinition.formReset();
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
		[
			isPetitionMemberAddressLookupModalOpen.value,
			petitionMemberAddressLookup.formDefinition.formMethods,
		]
	);

	useEffect(
		() => {
			if (
				selectedIcNumber &&
				formStep2PO?.formDefinition.formMethods.getFieldState('zakladatelPoIco')
					.isDirty
			) {
				const contact = rosCommunicationQuery.getRosContact(selectedIcNumber);

				setContactDetailsValues(formStep2PO.formDefinition, {
					_useEmail: !!contact?.email,
					_usePhone: !!contact?.telefonniCislo,
					email: contact?.email,
					phone: contact?.telefonniCislo
						? stringToPhoneShape(contact.telefonniCislo)
						: undefined,
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
		[selectedIcNumber]
	);

	const email =
		zakladatelTyp === 'FO'
			? formStep2FO?.formDefinition.formMethods.watch('email')
			: formStep2PO?.formDefinition.formMethods.watch('email');

	const phone =
		zakladatelTyp === 'FO'
			? formStep2FO?.formDefinition.formMethods.watch('phone')
			: formStep2PO?.formDefinition.formMethods.watch('phone');

	const resetEmailField = () => {
		formStep2FO.formDefinition.formMethods.resetField('email');
		formStep2PO.formDefinition.formMethods.resetField('email');
	};
	const resetPhoneField = () => {
		formStep2FO.formDefinition.formMethods.resetField('phone');
		formStep2PO.formDefinition.formMethods.resetField('phone');
	};

	const handleCreateSubmit = (onSuccess?: () => void) =>
		createFormDefinition.formMethods.handleSubmit(() => {
			wizard.submit(() => {
				createFormDefinition.formMethods.reset();
				if (onSuccess) onSuccess();
			});
		});

	return (
		<EPeticeEditContext.Provider
			value={{
				...(zakladatelTyp === 'PO'
					? {
							zakladatelTyp: 'PO',
							formStep2: formStep2PO,
							selectedIcNumber,
							selectIcNumberOptions,
						}
					: { zakladatelTyp: 'FO', formStep2: formStep2FO }),
				code,
				isProcessing:
					controls.processLoading ||
					controls.saveLoading ||
					controls.createLoading ||
					controls.deleteLoading,
				wizard,
				controls,
				petitionId,
				setControls,
				applicant,
				personCommunicationData,
				isEditable: petitionQuery.data
					? isPetitionEditable(petitionQuery.data)
					: false,
				isLowAuthorization,
				isAuthorizationEnforced,

				onSave: (onSuccess: () => void) => {
					saveMutation.mutate(undefined, {
						onSuccess,
					});
				},
				onDelete: (onSuccess: () => void) =>
					deleteMutation.mutate(undefined, { onSuccess }),
				onRemoveMember: (index: number) => {
					const existing =
						formStep2FO.formDefinition.formMethods.getValues(
							'clenovePeticnihoVyboru'
						) ?? [];

					formStep2FO.formDefinition.formMethods.setValue(
						'clenovePeticnihoVyboru',
						existing.filter((_, memberIndex) => memberIndex !== index)
					);
				},
				formStep1,

				createFormDefinition,
				handleCreateSubmit,
				addressLookup,
				petitionMemberAddressLookup,
				isPetitionMemberAddressLookupModalOpen,
				isSubmitConfirmModalOpen,
				isDeleteConfirmModalOpen,
				email,
				phone,
				resetEmailField,
				resetPhoneField,
			}}>
			{children}
		</EPeticeEditContext.Provider>
	);
};

export const useEPeticeEditContext = (): EPetitionEditContext =>
	useContext(EPeticeEditContext) as EPetitionEditContext;
